import { Key } from 'react';
import { Tooltip, Chip, TmdxAvatar } from '@ds';
import { formatSuggestionDate, SUGGESTED_STATUS_MAP } from '@lp/features/MultiSuggest/utils';
import { MultiSuggestEmployeeRoleList } from '@lp/features/MultiSuggest';
import { SuggestionSummaryType } from '@/api/apis/multi-suggest';

const MAX_REVIEW_COMMENT_LENGTH = 100;
const checkLongReviewComment = (comment: string) => comment.length > MAX_REVIEW_COMMENT_LENGTH;
const truncateReviewComment = (comment: string): string => {
  const ellipsis = checkLongReviewComment(comment) ? '...' : '';
  const truncatedComment = comment.substring(0, MAX_REVIEW_COMMENT_LENGTH);

  return `${truncatedComment}${ellipsis}`;
};

type Props = {
  item: SuggestionSummaryType;
  columnKey: Key;
};

const LastReviewCommentCell = ({ item }: Pick<Props, 'item'>) => {
  const lastReview = truncateReviewComment(item.lastReview || '');
  const isLongReview = checkLongReviewComment(item.lastReview || '');

  return (
    <Tooltip isDisabled={!isLongReview} showArrow classNames={{ base: 'max-w-120' }} content={item.lastReview || ''}>
      <span className="text-m-r max-w-[30ch]">{lastReview}</span>
    </Tooltip>
  );
};

export const MultiCaseSuggestTableCell = ({ item, columnKey }: Props) => {
  const key = columnKey as string;

  switch (key) {
    case 'userFullName':
      return (
        <div className="flex gap-2 items-center">
          <TmdxAvatar radius="xl" size="xl" name={item.user.fullName} src={item.user.photo} />
          <div className="flex flex-col">
            <span className="text-m-m">{item.user.fullName}</span>
            <MultiSuggestEmployeeRoleList roles={item.user.roles} />
          </div>
        </div>
      );
    case 'initialCase':
      return <Chip key={item.initialCase.caseId}>{item.initialCase.caseName}</Chip>;
    case 'targetCases':
      return (
        <div className="flex gap-2 flex-wrap">
          {item.targetCases.map((caseItem) => (
            <Chip key={caseItem.caseId}>{caseItem.caseName}</Chip>
          ))}
        </div>
      );
    case 'averageRating':
      return (
        <div className="flex gap-2 items-center">
          <span>★</span>
          <span className="text-m-r">{item.averageRating}</span>
        </div>
      );
    case 'suggestionStatus':
      return <span className="text-m-r">{SUGGESTED_STATUS_MAP[item.suggestionStatus]}</span>;
    case 'initiatedDate':
    case 'suggestedDate':
    case 'lastReviewDate':
      if (!item[key]) return <span className="text-m-r">N/A</span>;
      return <span className="text-m-r">{formatSuggestionDate(item[key])}</span>;
    case 'lastReview':
      return <LastReviewCommentCell item={item} />;
    default:
      return null;
  }
};
